import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";
import { usePageContent, getSection } from "@/hooks/usePageContent";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowRight, 
  Star,
  GraduationCap,
  MapPin,
  Quote,
  ChevronLeft,
  ChevronRight,
  Play,
  Filter,
  Sparkles,
  Award,
  Loader2
} from "lucide-react";

const API_URL = window.location.origin;

// Default success stories data (fallback)
const defaultSuccessStories = [
  {
    id: 1,
    name: 'Priya Sharma',
    image: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/b49ec939dcc99e8554de63ada9b2850e23d14f15de24b5d9d4abb64fd3f3aa9c.png',
    university: 'Massachusetts Institute of Technology',
    universityShort: 'MIT',
    country: 'USA',
    flag: '🇺🇸',
    program: 'MS Computer Science',
    year: '2024',
    previousEducation: 'B.Tech from IIT Delhi',
    quote: 'UniXperts made my dream of studying at MIT a reality. Their guidance on SOP and interview prep was invaluable. I got admits from 5 top universities!',
    scholarship: '$40,000/year',
    rating: 5,
    videoUrl: null,
    tags: ['STEM', 'Scholarship Winner', 'Top 10 University']
  },
  {
    id: 2,
    name: 'Rahul Verma',
    image: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/4a400db3708b85c206d4e700438c579ce94123ecdcb42fdcfe91e661db03a37b.png',
    university: 'University of Oxford',
    universityShort: 'Oxford',
    country: 'UK',
    flag: '🇬🇧',
    program: 'MBA',
    year: '2024',
    previousEducation: 'B.Com from SRCC, Delhi',
    quote: 'The team helped me craft a compelling story for my MBA application. Their mock interviews prepared me perfectly for Oxford. Forever grateful!',
    scholarship: '£25,000 total',
    rating: 5,
    videoUrl: 'https://youtube.com/watch?v=example',
    tags: ['MBA', 'Scholarship Winner', 'Top 5 University']
  },
  {
    id: 3,
    name: 'Ananya Patel',
    image: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/2235dcf9d6cbba7c8ada0df52dd30c647dbfc2eab8e2a909ad9e0e19166d7f3f.png',
    university: 'University of Toronto',
    universityShort: 'UofT',
    country: 'Canada',
    flag: '🇨🇦',
    program: 'MS Data Science',
    year: '2023',
    previousEducation: 'B.Sc from St. Xavier\'s Mumbai',
    quote: 'From university shortlisting to visa approval, UniXperts handled everything seamlessly. Now I\'m thriving in Toronto with a part-time job too!',
    scholarship: 'CAD 15,000/year',
    rating: 5,
    videoUrl: null,
    tags: ['Data Science', 'Canada PR Pathway', 'Working Student']
  },
  {
    id: 4,
    name: 'Vikram Singh',
    image: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/07d75fc36fa95383b02907ab0a5040713a285b750b593344711ebb2f273d8dff.png',
    university: 'Technical University of Munich',
    universityShort: 'TU Munich',
    country: 'Germany',
    flag: '🇩🇪',
    program: 'MS Automotive Engineering',
    year: '2024',
    previousEducation: 'B.Tech from NIT Trichy',
    quote: 'Studying in Germany for almost free was a dream. UniXperts guided me through the German university system and blocked account process perfectly.',
    scholarship: 'Tuition Free',
    rating: 5,
    videoUrl: null,
    tags: ['Engineering', 'Tuition Free', 'Automotive']
  },
  {
    id: 5,
    name: 'Sneha Reddy',
    image: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/016100629512cff6e03f122812bab0d5cddaa7e2cf4c48d44e50fbd45c092236.png',
    university: 'University of Melbourne',
    universityShort: 'Melbourne',
    country: 'Australia',
    flag: '🇦🇺',
    program: 'Master of Public Health',
    year: '2023',
    previousEducation: 'MBBS from AIIMS',
    quote: 'As a doctor wanting to specialize in public health, UniXperts found the perfect program for me. Their visa assistance was exceptional!',
    scholarship: 'AUD 20,000',
    rating: 5,
    videoUrl: 'https://youtube.com/watch?v=example2',
    tags: ['Healthcare', 'Medical Graduate', 'Research Focus']
  },
  {
    id: 6,
    name: 'Arjun Nair',
    image: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/255242dd86ceb1e9ab4588648c0ac5677418800a9865c223139ac811a7dbbceb.png',
    university: 'Stanford University',
    universityShort: 'Stanford',
    country: 'USA',
    flag: '🇺🇸',
    program: 'MS Electrical Engineering',
    year: '2024',
    previousEducation: 'B.Tech from BITS Pilani',
    quote: 'I never thought I could get into Stanford. UniXperts believed in me and helped me present my profile in the best way possible.',
    scholarship: 'Full Funding',
    rating: 5,
    videoUrl: null,
    tags: ['STEM', 'Full Scholarship', 'Silicon Valley']
  },
  {
    id: 7,
    name: 'Meera Krishnan',
    image: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/3ba138e7e00463e05def77675d4f20fa8ac9447b1e060a0d38e1c6863d27202e.png',
    university: 'University of Auckland',
    universityShort: 'Auckland',
    country: 'New Zealand',
    flag: '🇳🇿',
    program: 'MS Environmental Science',
    year: '2023',
    previousEducation: 'B.Sc from Loyola Chennai',
    quote: 'New Zealand was the perfect choice for my environmental science studies. UniXperts helped me discover this gem of a destination!',
    scholarship: 'NZD 10,000',
    rating: 5,
    videoUrl: null,
    tags: ['Environmental', 'New Zealand', 'Research']
  },
  {
    id: 8,
    name: 'Karthik Iyer',
    image: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/38636149760adfbbd83fb8dd2fd274ad3082125c16fba0258b2fc3a000e84faf.png',
    university: 'Imperial College London',
    universityShort: 'Imperial',
    country: 'UK',
    flag: '🇬🇧',
    program: 'MSc Finance',
    year: '2024',
    previousEducation: 'CA + B.Com from Mumbai',
    quote: 'Transitioning from CA to finance at Imperial was smooth thanks to UniXperts. Their understanding of UK admissions is unmatched.',
    scholarship: '£15,000',
    rating: 5,
    videoUrl: null,
    tags: ['Finance', 'Career Change', 'London']
  }
];

// Default Stats
const defaultStats = [
  { value: '30,000+', label: 'Students Placed' },
  { value: '98%', label: 'Visa Success Rate' },
  { value: '500+', label: 'Partner Universities' },
  { value: '15', label: 'Countries' },
];

// Default Filter options
const defaultFilterOptions = {
  countries: ['All', 'USA', 'UK', 'Canada', 'Australia', 'Germany', 'New Zealand'],
  programs: ['All', 'STEM', 'MBA', 'Healthcare', 'Finance', 'Engineering'],
};

export const SuccessStoriesPage = () => {
  const { isDark } = useTheme();
  const [selectedCountry, setSelectedCountry] = useState('All');
  const [selectedProgram, setSelectedProgram] = useState('All');
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // CMS State
  const [stories, setStories] = useState(defaultSuccessStories);
  const [stats, setStats] = useState(defaultStats);
  const [filterOptions, setFilterOptions] = useState(defaultFilterOptions);
  
  // CMS content for page-level sections (hero, headers, final CTA)
  const { content: cmsContent } = usePageContent('success-stories');
  const heroContent = getSection(cmsContent, 'hero', {
    badge: 'Success Stories',
    title: 'Dreams Turned Into',
    title_highlight: 'Reality',
    subtitle: 'Real stories from real students who achieved their study abroad dreams with UniXperts. Your success story could be next!'
  });
  const featuredHeader = getSection(cmsContent, 'featured_header', {
    badge: 'Featured Story',
    title: 'Student',
    title_highlight: 'Spotlight'
  });
  const allStoriesHeader = getSection(cmsContent, 'all_stories_header', {
    title: 'All Success',
    title_highlight: 'Stories',
    subtitle: 'Filter by country or program to find stories like yours'
  });
  const finalCta = getSection(cmsContent, 'final_cta', {
    title: 'Ready to Write Your Success Story?',
    subtitle: 'Join thousands of students who achieved their dreams with UniXperts. Your success story starts here!',
    cta_primary_text: 'Book Free Consultation',
    cta_primary_link: '/contact',
    cta_secondary_text: 'Talk to a Counsellor',
    cta_secondary_link: 'mailto:counsel@unixperts.com?subject=Interested%20in%20studying%20abroad'
  });
  
  // Fetch success stories from CMS
  useEffect(() => {
    const fetchSuccessStories = async () => {
      try {
        const res = await fetch(`${API_URL}/api/cms/site-settings/success-stories`);
        if (res.ok) {
          const data = await res.json();
          if (data.stories && data.stories.length > 0) {
            // Map CMS field names to component expected names
            const mappedStories = data.stories
              .filter(s => s.is_active !== false)
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map(s => ({
                ...s,
                universityShort: s.university_short || s.universityShort,
                previousEducation: s.previous_education || s.previousEducation,
                videoUrl: s.video_url || s.videoUrl
              }));
            setStories(mappedStories);
          }
          if (data.stats && data.stats.length > 0) {
            setStats(data.stats);
          }
          if (data.filter_countries && data.filter_programs) {
            setFilterOptions({
              countries: data.filter_countries,
              programs: data.filter_programs
            });
          }
        }
      } catch (error) {
        console.error('Error fetching success stories:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSuccessStories();
  }, []);

  // Filter stories
  const filteredStories = stories.filter((story) => {
    const countryMatch = selectedCountry === 'All' || story.country === selectedCountry;
    const programMatch = selectedProgram === 'All' || story.tags?.some(tag => 
      tag.toLowerCase().includes(selectedProgram.toLowerCase())
    );
    return countryMatch && programMatch;
  });

  const featuredStory = stories[featuredIndex];

  const nextFeatured = () => {
    setFeaturedIndex((prev) => (prev + 1) % stories.length);
  };

  const prevFeatured = () => {
    setFeaturedIndex((prev) => (prev - 1 + stories.length) % stories.length);
  };

  return (
    <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#1a2830]' : 'bg-white'} transition-colors duration-500`} data-testid="success-stories-page">
      {/* Hero Section */}
      <section className={`py-20 md:py-28 relative overflow-hidden ${isDark ? 'bg-gradient-to-br from-[#1a2830] via-[#233842] to-[#1a2830]' : 'bg-gradient-to-br from-ux-charcoal via-ux-charcoal to-ux-violet-dark'}`}>
        <div className="absolute top-0 right-0 w-96 h-96 bg-ux-cyan/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-ux-coral/20 rounded-full blur-3xl" />
        <div className="absolute top-1/3 left-1/4 w-48 h-48 bg-ux-amber/10 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-panel mb-6">
              <Award className="w-4 h-4 text-ux-amber" />
              <span className="text-sm font-medium text-white">{heroContent.badge}</span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold mb-6 text-white tracking-tight" data-testid="success-hero-title">
              {heroContent.title}
              <span className="gradient-text-aurora"> {heroContent.title_highlight}</span>
            </h1>
            <p className="text-lg md:text-xl text-white/80 leading-relaxed">
              {heroContent.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-8 bg-gradient-to-r from-ux-violet via-ux-cyan to-ux-blue">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-white text-center">
            {stats.map((stat) => (
              <div key={stat.label}>
                <p className="font-display text-2xl md:text-4xl font-bold">{stat.value}</p>
                <p className="text-sm text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Story */}
      <section className={`py-16 md:py-24 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className={`mb-4 ${isDark ? 'bg-ux-violet/20 text-ux-violet' : 'bg-ux-violet/10 text-ux-violet'}`}>
              {featuredHeader.badge}
            </Badge>
            <h2 className={`font-display text-3xl md:text-4xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`} data-testid="featured-header-title">
              {featuredHeader.title}
              <span className="gradient-text-electric"> {featuredHeader.title_highlight}</span>
            </h2>
          </div>

          <div className="glass-panel rounded-3xl overflow-hidden">
            <div className="grid md:grid-cols-2">
              {/* Image Side */}
              <div className="relative h-64 md:h-auto min-h-[400px]">
                <img 
                  src={featuredStory.image}
                  alt={featuredStory.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/30 md:bg-gradient-to-l" />
                
                {/* Navigation */}
                <div className="absolute bottom-4 left-4 flex gap-2">
                  <button 
                    onClick={prevFeatured}
                    className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors"
                    aria-label="Previous story"
                  >
                    <ChevronLeft className="h-5 w-5 text-white" />
                  </button>
                  <button 
                    onClick={nextFeatured}
                    className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors"
                    aria-label="Next story"
                  >
                    <ChevronRight className="h-5 w-5 text-white" />
                  </button>
                </div>

                {featuredStory.videoUrl && (
                  <button className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-ux-coral flex items-center justify-center hover:bg-ux-coral-dark transition-colors shadow-glow-coral hover:scale-110">
                    <Play className="h-6 w-6 text-white ml-1" />
                  </button>
                )}
              </div>

              {/* Content Side */}
              <div className="p-8 md:p-12">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-4xl">{featuredStory.flag}</span>
                  <div>
                    <h3 className={`font-display text-2xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{featuredStory.name}</h3>
                    <p className={isDark ? 'text-white/70' : 'text-ux-charcoal/70'}>{featuredStory.previousEducation}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-6">
                  <GraduationCap className="h-5 w-5 text-ux-cyan" />
                  <span className={`font-semibold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{featuredStory.university}</span>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  <Badge className="bg-ux-cyan/20 text-ux-cyan border-ux-cyan/30">
                    {featuredStory.program}
                  </Badge>
                  <Badge className="bg-ux-coral/20 text-ux-coral border-ux-coral/30">
                    {featuredStory.scholarship}
                  </Badge>
                  <Badge className={`${isDark ? 'bg-white/20 text-white border-white/30' : 'bg-ux-charcoal/10 text-ux-charcoal border-ux-charcoal/20'}`}>
                    {featuredStory.year}
                  </Badge>
                </div>

                <div className="relative mb-6">
                  <Quote className="absolute -top-2 -left-2 h-8 w-8 text-ux-cyan/30" />
                  <p className={`text-lg italic pl-6 ${isDark ? 'text-white/90' : 'text-ux-charcoal/90'}`}>
                    "{featuredStory.quote}"
                  </p>
                </div>

                <div className="flex items-center gap-1 mb-6">
                  {[...Array(featuredStory.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-ux-amber text-ux-amber" />
                  ))}
                </div>

                <div className="flex flex-wrap gap-2">
                  {featuredStory.tags.map((tag) => (
                    <Badge key={tag} variant="outline" className={isDark ? 'border-white/30 text-white/80' : ''}>
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters & All Stories */}
      <section className={`py-16 md:py-24 ${isDark ? 'bg-[#233842]' : 'bg-ux-slate-light'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-12">
            <div>
              <h2 className={`font-display text-3xl md:text-4xl font-bold mb-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`} data-testid="all-stories-header-title">
                {allStoriesHeader.title}
                <span className="gradient-text-brand"> {allStoriesHeader.title_highlight}</span>
              </h2>
              <p className={isDark ? 'text-white/70' : 'text-ux-charcoal/70'}>
                {allStoriesHeader.subtitle}
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Filter className={`h-4 w-4 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`} />
                <select 
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value)}
                  className={`px-4 py-2 rounded-full border ${isDark ? 'bg-white/5 border-white/20 text-white' : 'bg-white border-gray-200 text-ux-charcoal'} focus:outline-none focus:ring-2 focus:ring-ux-cyan`}
                >
                  {filterOptions.countries.map((country) => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
              <select 
                value={selectedProgram}
                onChange={(e) => setSelectedProgram(e.target.value)}
                className={`px-4 py-2 rounded-full border ${isDark ? 'bg-white/5 border-white/20 text-white' : 'bg-white border-gray-200 text-ux-charcoal'} focus:outline-none focus:ring-2 focus:ring-ux-cyan`}
              >
                {filterOptions.programs.map((program) => (
                  <option key={program} value={program}>{program}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredStories.map((story) => (
              <div 
                key={story.id} 
                className="glass-panel rounded-3xl overflow-hidden hover:scale-[1.02] transition-all group"
                data-testid={`story-card-${story.id}`}
              >
                <div className="relative h-48">
                  <img 
                    src={story.image}
                    alt={story.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <div className="flex items-center gap-2 text-white">
                      <span className="text-2xl">{story.flag}</span>
                      <div>
                        <p className="font-display font-bold">{story.name}</p>
                        <p className="text-sm text-white/80">{story.universityShort}</p>
                      </div>
                    </div>
                  </div>
                  {story.videoUrl && (
                    <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-ux-coral/90 flex items-center justify-center hover:bg-ux-coral transition-colors hover:scale-110">
                      <Play className="h-4 w-4 text-white ml-0.5" />
                    </button>
                  )}
                </div>

                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge className={`${isDark ? 'bg-ux-violet/20 text-ux-violet' : 'bg-ux-violet/10 text-ux-violet'}`}>
                      {story.program}
                    </Badge>
                    <Badge className={`${isDark ? 'bg-ux-cyan/20 text-ux-cyan' : 'bg-ux-cyan/10 text-ux-cyan'}`}>
                      {story.scholarship}
                    </Badge>
                  </div>

                  <p className={`text-sm mb-4 line-clamp-3 ${isDark ? 'text-white/70' : 'text-ux-charcoal/70'}`}>
                    "{story.quote}"
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      {[...Array(story.rating)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-ux-amber text-ux-amber" />
                      ))}
                    </div>
                    <span className={`text-xs ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>Class of {story.year}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredStories.length === 0 && (
            <div className="text-center py-12">
              <p className={isDark ? 'text-white/70' : 'text-ux-charcoal/70'}>No stories found for the selected filters.</p>
              <Button 
                variant="link" 
                onClick={() => { setSelectedCountry('All'); setSelectedProgram('All'); }}
                className="text-ux-cyan"
              >
                Clear filters
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-ux-blue via-ux-violet to-ux-cyan" />
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.4"%3E%3Cpath d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }} />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="font-display text-3xl md:text-5xl font-bold mb-6 text-white" data-testid="success-final-cta-title">
            {finalCta.title}
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            {finalCta.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-white text-ux-violet rounded-full px-8 py-6 text-lg font-bold hover:bg-ux-green hover:text-ux-charcoal hover:scale-105 transition-all"
              asChild
            >
              <Link to={finalCta.cta_primary_link && !finalCta.cta_primary_link.startsWith('/uac-desk') ? finalCta.cta_primary_link : '/contact'}>
                {finalCta.cta_primary_text}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button 
              size="lg"
              variant="hero-outline"
              className="rounded-full px-8 py-6 text-lg font-medium"
              asChild
            >
              {(finalCta.cta_secondary_link || '').startsWith('mailto:') || (finalCta.cta_secondary_link || '').startsWith('http') ? (
                <a href={finalCta.cta_secondary_link}>{finalCta.cta_secondary_text}</a>
              ) : (
                <Link to={finalCta.cta_secondary_link || '/contact'}>{finalCta.cta_secondary_text}</Link>
              )}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SuccessStoriesPage;
